import { CompanyType } from "@/Enums/CompanyType";
import { createSingleton, Singleton } from "vig-ui-library";

export enum EnvironmentVariable {
    BASE_URL = "EnvBaseUrl",
    COMPANY_TYPE = "EnvCompanyType",
}

const getEnvironmentVariable = (variable: EnvironmentVariable): string => {
    const metaElement: HTMLMetaElement | null = document.querySelector(`meta[name="${variable}"]`);
    return metaElement?.content || "";
};

const getMetaTagContent = (name: string): string | null => {
    const metaElement: HTMLMetaElement | null = document.querySelector(`meta[name="${name}"]`);
    return metaElement?.content || null;
};

export const getRecaptchaSiteKey: Singleton<string | null> = createSingleton((): string | null => {
    return getMetaTagContent("EnvRecaptchaSiteKey");
});

export const getHelpUrl: Singleton<string | null> = createSingleton((): string | null => {
    return getMetaTagContent("EnvHelpUrl");
});

export const getRXReturnUrl = (
    requestMasterTypeId: number
): string | null => {
    return getMetaTagContent(`EnvR${requestMasterTypeId}ReturnUrl`);
};

const getSettingWithDefaults = (settingName: string, koopDefault: string, kpasDefault: string) => {
    const callcentrumNumber = getMetaTagContent(settingName);
    if (!!callcentrumNumber) return callcentrumNumber;
    switch (companyType) {
        case CompanyType.KOOPERATIVA:
            return koopDefault;
        case CompanyType.KOMUNALNA:
            return kpasDefault;
        default:
            return "";
    }
}

export const getCallcentrumTelNumber: Singleton<string> = createSingleton((): string => getSettingWithDefaults("CallcentrumTelNumber", "+421 2 5729 9999", "0800 112 222"));
export const getCallcentrumOpenningHoursFrom: Singleton<string> = createSingleton((): string => getSettingWithDefaults("CallcentrumOpenningHoursFrom", "8:00", "9:00"));
export const getCallcentrumOpenningHoursTo: Singleton<string> = createSingleton((): string => getSettingWithDefaults("CallcentrumOpenningHoursTo", "17:00", "16:00"));

export const baseURL = getEnvironmentVariable(EnvironmentVariable.BASE_URL);

const parseCompanyType = (companyType: string): CompanyType | null => {
    const parsedCompanyType = parseInt(companyType);
    if (isNaN(parsedCompanyType)) return null;
    switch (parsedCompanyType) {
        case CompanyType.KOOPERATIVA:
        case CompanyType.KOMUNALNA:
            return parsedCompanyType;
        default:
            return null;
    }
}

export const companyType = parseCompanyType(getEnvironmentVariable(EnvironmentVariable.COMPANY_TYPE));

// isPublicApp is set at build time via Vite's define option
// It will be true for the public app build and false for the standard build
export const isPublicApp: boolean = Boolean(import.meta.env.IS_PUBLIC_APP);

export const getCompanyLogoUrl = () => {
    switch (companyType) {
        case CompanyType.KOMUNALNA:
            return "https://static.kpas.sk/pics/Kpas-logo.png";
        case CompanyType.KOOPERATIVA:
        default:
            return "https://static.koop.sk/pics/Kooperativa.svg";
    }
}

export const getCompanyLogoAlt = () => {
    switch (companyType) {
        case CompanyType.KOMUNALNA:
            return "Komunálna poisťovňa: Vienna Insurance Group";
        case CompanyType.KOOPERATIVA:
        default:
            return "Kooperativa: Vienna Insurance Group";
    }
};

export const getPortalUrl = (): string | null => {
    const portalUrl = getMetaTagContent("portal-url")?.trim();
    return portalUrl || null;
};

export const getCompanyHomeUrl = (): string => {
    switch (companyType) {
        case CompanyType.KOMUNALNA:
            return "https://www.kpas.sk";
        case CompanyType.KOOPERATIVA:
        default:
            return "https://www.kooperativa.sk";
    }
};

export const getCompanyHomeLabel = (): string => {
    switch (companyType) {
        case CompanyType.KOMUNALNA:
            return "kpas.sk";
        case CompanyType.KOOPERATIVA:
        default:
            return "kooperativa.sk";
    }
};

export const getCompanyTitle = (): string => {
    switch (companyType) {
        case CompanyType.KOMUNALNA:
            return "Komunálna poisťovňa";
        case CompanyType.KOOPERATIVA:
        default:
            return "Kooperativa";
    }
}

export const getCompanyPortalName = () => {
    switch (companyType) {
        case CompanyType.KOMUNALNA:
            return "e-KOMfort";
        case CompanyType.KOOPERATIVA:
        default:
            return "eKooperativa";
    }
}
